/**
 * Webhook Activity tab: recent incoming trigger attempts (GET /incoming-webhook/activity).
 * Top-level page (route /webhook-activity), moved out of the Settings page.
 * Transmitted payloads are masked; reveal requires password re-authentication.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import { Refresh as RefreshIcon, Visibility as VisibilityIcon } from '@mui/icons-material';
import { apiService, ApiError } from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';
import type { IncomingWebhookActivityRow } from '../../types/dashboard';
import PageContainer from '../layout/PageContainer';
import PageHeader, { Accent } from '../layout/PageHeader';
import ReauthDialog from '../common/ReauthDialog';

function activityPhaseColor(phase: string): 'default' | 'success' | 'error' | 'warning' | 'info' {
  if (phase === 'success' || phase === 'completed') return 'success';
  if (phase === 'pending' || phase === 'waiting') return 'info';
  if (phase === 'validation_error') return 'warning';
  if (phase === 'unauthorized' || phase === 'forbidden') return 'error';
  if (phase === 'execution_error') return 'error';
  if (phase === 'rate_limited') return 'info';
  return 'default';
}

function formatPayloadPreview(payload: string | null | undefined): string {
  if (!payload) return '—';
  const trimmed = payload.trim();
  if (!trimmed) return '—';
  if (trimmed === '[REDACTED]') return '[REDACTED]';
  try {
    const parsed = JSON.parse(trimmed) as unknown;
    if (typeof parsed === 'object' && parsed !== null) {
      const obj = parsed as Record<string, unknown>;
      if (typeof obj.incoming_webhook_text === 'string') {
        return obj.incoming_webhook_text;
      }
      if (typeof obj.payload === 'string') {
        return obj.payload;
      }
      return JSON.stringify(parsed, null, 2);
    }
    return trimmed;
  } catch {
    return trimmed;
  }
}

function formatAlertLabel(row: IncomingWebhookActivityRow): string {
  if (row.alert_name?.trim()) return row.alert_name.trim();
  if (row.alert_stock_symbol?.trim()) return row.alert_stock_symbol.trim();
  if (row.alert_configuration_id != null) return `Alert #${row.alert_configuration_id}`;
  return '—';
}

function formatDirectionLabel(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const value = raw.trim().toLowerCase();
  if (value === 'up') return 'Bullish';
  if (value === 'down') return 'Bearish';
  if (value === 'neutral') return 'Neutral';
  return raw.trim();
}

function formatSlotDirection(row: IncomingWebhookActivityRow): string {
  const parts: string[] = [];
  const indicator =
    row.slot_label?.trim() ||
    (row.slot_index != null ? `Indicator ${row.slot_index + 1}` : '');
  if (indicator) {
    parts.push(indicator);
  }
  const direction = formatDirectionLabel(
    row.resolved_direction || row.bmta_direction_from_token
  );
  if (direction) {
    parts.push(direction);
  }
  return parts.length > 0 ? parts.join(' · ') : '—';
}

function formatActivityWhen(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

type PayloadKind = 'incoming' | 'outgoing';

function PayloadCell({
  revealedText,
  stored,
  present,
  maskedFlag,
  onReveal,
}: {
  revealedText?: string;
  stored?: string | null;
  present?: boolean;
  maskedFlag?: boolean;
  onReveal: () => void;
}) {
  const isMasked =
    revealedText == null && (maskedFlag || stored === '[REDACTED]' || present);
  return (
    <TableCell
      sx={{ maxWidth: 320, wordBreak: 'break-word', fontFamily: 'monospace', fontSize: '0.75rem' }}
    >
      {revealedText != null ? (
        formatPayloadPreview(revealedText)
      ) : isMasked ? (
        <Box>
          <Typography component="span" variant="body2" color="text.secondary">
            [REDACTED]
          </Typography>
          <Button
            size="small"
            startIcon={<VisibilityIcon />}
            onClick={onReveal}
            sx={{ ml: 1, verticalAlign: 'middle' }}
          >
            Reveal
          </Button>
        </Box>
      ) : (
        formatPayloadPreview(stored)
      )}
    </TableCell>
  );
}

const WebhookActivityPage: React.FC = () => {
  const { token: authToken } = useAuth();
  const [rows, setRows] = useState<IncomingWebhookActivityRow[]>([]);
  const [revealedIncoming, setRevealedIncoming] = useState<Record<number, string>>({});
  const [revealedOutgoing, setRevealedOutgoing] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reauthOpen, setReauthOpen] = useState(false);
  const [revealTargetId, setRevealTargetId] = useState<number | null>(null);
  const [revealKind, setRevealKind] = useState<PayloadKind>('incoming');

  const load = useCallback(async () => {
    if (!authToken) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.getIncomingWebhookActivity(authToken, 100, 0);
      if (res.success) {
        const payload = res.data;
        setRows(Array.isArray(payload) ? (payload as IncomingWebhookActivityRow[]) : []);
        setRevealedIncoming({});
        setRevealedOutgoing({});
      } else {
        setError(res.error || 'Failed to load activity');
      }
    } catch (e: unknown) {
      const msg =
        e instanceof ApiError
          ? e.message
          : e instanceof Error
            ? e.message
            : 'Failed to load activity';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleRevealClick = (activityId: number, kind: PayloadKind) => {
    setRevealTargetId(activityId);
    setRevealKind(kind);
    setReauthOpen(true);
  };

  const handleReauthSuccess = async (stepUpToken: string) => {
    if (revealTargetId == null) return;
    const kind = revealKind;
    try {
      const res = await apiService.revealIncomingWebhookActivityPayload(
        revealTargetId,
        stepUpToken,
        authToken || undefined,
        kind
      );
      if (res.success) {
        const text =
          kind === 'outgoing'
            ? res.data?.outgoing_payload ?? ''
            : res.data?.request_payload ?? '';
        const apply = (prev: Record<number, string>) => ({
          ...prev,
          [revealTargetId]: text,
        });
        if (kind === 'outgoing') {
          setRevealedOutgoing(apply);
        } else {
          setRevealedIncoming(apply);
        }
      } else {
        setError(res.error || 'Failed to reveal payload');
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to reveal payload');
    } finally {
      setRevealTargetId(null);
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Webhook activity"
        subtitle={
          <>
            Recent incoming trigger attempts. Incoming and outgoing payloads are{' '}
            <Accent>masked</Accent> until you re-authenticate to reveal them.
          </>
        }
        actions={
          <Button
            startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
            onClick={() => void load()}
            disabled={loading}
            variant="outlined"
          >
            Refresh
          </Button>
        }
      />
      <Box>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <Card>
          <CardContent>
            {loading && rows.length === 0 ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : rows.length === 0 ? (
              <Typography color="text.secondary">No incoming webhook activity yet.</Typography>
            ) : (
              <TableContainer sx={{ maxHeight: 640 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>When</TableCell>
                      <TableCell>HTTP</TableCell>
                      <TableCell>Phase</TableCell>
                      <TableCell>Alert</TableCell>
                      <TableCell>Slot / direction</TableCell>
                      <TableCell>Incoming payload</TableCell>
                      <TableCell>Outgoing payload</TableCell>
                      <TableCell>Execution</TableCell>
                      <TableCell>Summary</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((r) => {
                      return (
                        <TableRow key={r.id} hover>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatActivityWhen(r.created_at)}</TableCell>
                          <TableCell>{r.http_status}</TableCell>
                          <TableCell>
                            <Chip size="small" label={r.phase} color={activityPhaseColor(r.phase)} variant="outlined" />
                          </TableCell>
                          <TableCell sx={{ maxWidth: 180, wordBreak: 'break-word' }}>
                            {formatAlertLabel(r)}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {formatSlotDirection(r)}
                            {r.token_fingerprint_suffix ? (
                              <Typography component="span" variant="caption" display="block" color="text.secondary">
                                token {r.token_fingerprint_suffix}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <PayloadCell
                            revealedText={revealedIncoming[r.id]}
                            stored={r.request_payload}
                            present={r.payload_present}
                            maskedFlag={r.payload_masked}
                            onReveal={() => handleRevealClick(r.id, 'incoming')}
                          />
                          <PayloadCell
                            revealedText={revealedOutgoing[r.id]}
                            stored={r.outgoing_payload}
                            present={r.outgoing_payload_present}
                            maskedFlag={r.outgoing_payload_masked}
                            onReveal={() => handleRevealClick(r.id, 'outgoing')}
                          />
                          <TableCell>
                            {r.execution_status ?? '—'}
                            {r.webhooks_executed != null ? (
                              <Typography variant="caption" display="block" color="text.secondary">
                                ok {r.successful ?? 0} / fail {r.failed ?? 0} / total {r.webhooks_executed}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 360, wordBreak: 'break-word' }}>
                            {r.summary_message ?? '—'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Box>
      <ReauthDialog
        open={reauthOpen}
        onClose={() => {
          setReauthOpen(false);
          setRevealTargetId(null);
        }}
        onSuccess={(stepUp) => {
          void handleReauthSuccess(stepUp);
        }}
        description={
          revealKind === 'outgoing'
            ? 'Re-enter your account password to view the payload sent to each destination.'
            : 'Re-enter your account password to view this transmitted payload.'
        }
      />
    </PageContainer>
  );
};

export default WebhookActivityPage;
